// qa-agent's own introspection snapshot predates the text attachment enum
// values, so these come from react-client's generated schema instead.
import type {
  AttachmentType,
  FileType,
} from '@lifeomic/react-client/generated/graphql/graphql';

/**
 * How many files may ride along with a single message. Each one is downloaded,
 * decoded, and inlined into the prompt by the agent, so the cap is about the
 * turn's context budget rather than any upload limit.
 */
export const MAX_CHAT_ATTACHMENTS = 4;

/**
 * Per-file size cap. PDFs are rasterized to one image per page before reaching
 * the model, so a large file is far more expensive than its byte count implies.
 */
export const MAX_CHAT_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * Content types the agent can actually read.
 *
 * Deliberately narrower than forum-service's `FileType`: GIF and video upload
 * fine but the agent resolves no content type for them, so their content blocks
 * are dropped server-side and the file vanishes silently from the conversation.
 * Better to refuse them here than to accept them and lose them.
 */
export const SUPPORTED_ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/x-markdown',
  'application/json',
  'text/json',
] as const;

export type SupportedAttachmentContentType =
  (typeof SUPPORTED_ATTACHMENT_CONTENT_TYPES)[number];

export type AttachmentUploadConfig = {
  fileType: FileType;
  attachmentType: AttachmentType;
};

const UPLOAD_CONFIG_BY_CONTENT_TYPE: Record<
  SupportedAttachmentContentType,
  AttachmentUploadConfig
> = {
  'application/pdf': { fileType: 'PDF', attachmentType: 'PDF' },
  'image/png': { fileType: 'PNG', attachmentType: 'IMAGE' },
  'image/jpeg': { fileType: 'JPEG', attachmentType: 'IMAGE' },
  'text/plain': { fileType: 'TXT', attachmentType: 'TXT' },
  'text/csv': { fileType: 'CSV', attachmentType: 'CSV' },
  'text/markdown': { fileType: 'MD', attachmentType: 'MD' },
  'text/x-markdown': { fileType: 'MD', attachmentType: 'MD' },
  'application/json': { fileType: 'JSON', attachmentType: 'JSON' },
  'text/json': { fileType: 'JSON', attachmentType: 'JSON' },
};

/** Strips any `; charset=…` parameter and normalizes case. */
export const normalizeContentType = (contentType: string | undefined) =>
  contentType?.split(';')[0]?.trim().toLowerCase();

export const isSupportedAttachmentContentType = (
  contentType: string | undefined,
): contentType is SupportedAttachmentContentType =>
  SUPPORTED_ATTACHMENT_CONTENT_TYPES.includes(
    contentType as SupportedAttachmentContentType,
  );

/**
 * Browsers leave `File.type` empty for types the OS does not recognize — most
 * often Markdown — so the extension is the more reliable signal.
 */
export const inferContentTypeFromFilename = (
  fileName: string,
): SupportedAttachmentContentType | undefined => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (/\.jpe?g$/.test(lower)) return 'image/jpeg';
  if (lower.endsWith('.csv')) return 'text/csv';
  if (lower.endsWith('.txt')) return 'text/plain';
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) {
    return 'text/markdown';
  }
  if (lower.endsWith('.json')) return 'application/json';
  return undefined;
};

/**
 * Best supported content type for a picked file, preferring what the browser
 * reported and falling back to the file extension.
 */
export const resolveAttachmentContentType = (file: {
  name: string;
  type?: string;
}): SupportedAttachmentContentType | undefined => {
  const declared = normalizeContentType(file.type);
  return isSupportedAttachmentContentType(declared)
    ? declared
    : inferContentTypeFromFilename(file.name);
};

export const getAttachmentUploadConfig = (
  contentType: string | undefined,
): AttachmentUploadConfig | undefined => {
  const normalized = normalizeContentType(contentType);
  return isSupportedAttachmentContentType(normalized)
    ? UPLOAD_CONFIG_BY_CONTENT_TYPE[normalized]
    : undefined;
};

export type AttachmentRejectionReason =
  | 'unsupported-type'
  | 'too-large'
  | 'empty';

export type AttachmentValidationResult =
  | { ok: true; contentType: SupportedAttachmentContentType }
  | { ok: false; reason: AttachmentRejectionReason };

export const validateAttachmentFile = (file: {
  name: string;
  type?: string;
  size: number;
}): AttachmentValidationResult => {
  const contentType = resolveAttachmentContentType(file);
  if (!contentType) {
    return { ok: false, reason: 'unsupported-type' };
  }
  if (file.size === 0) {
    return { ok: false, reason: 'empty' };
  }
  if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
    return { ok: false, reason: 'too-large' };
  }
  return { ok: true, contentType };
};

/** `accept` value for the file picker, so the OS dialog filters up front. */
export const ATTACHMENT_FILE_PICKER_ACCEPT = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.txt',
  '.csv',
  '.md',
  '.markdown',
  '.json',
].join(',');
